import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import * as Yup from 'yup';
import { fetchFormFields, postFormData } from '../../../../modules/api';
import { useRecaptchaToken } from '../../../globalHooks/useRecaptchaToken'; // Adjust path as needed

const useNewsletterForm = () => {
  const [fields, setFields] = useState({});

  const [waiting, setWaiting] = useState(true);

  const [submitInfo, setSubmitInfo] = useState(null);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const [footerNewsletterInitialValues, setFooterNewsletterInitialValues] =
    useState({
      first_name: '',
      last_name: '',
      email: '',
      opt_in: 'yes',
      honeypot: null,
      current_page: window.location.href,
      referrer: document.referrer,
      client_id: 'No client id detected',
    });

  const [cookies] = useCookies(['_ga']);
  const executeRecaptcha = useRecaptchaToken();

  const footerNewsletterValidationSchema = Yup.object({
    first_name: Yup.string().required('* required'),
    last_name: Yup.string().required('* required'),
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('* required'),
  });

  // Get _ga client id
  const getGaClientId = () => {
    const gaCookie = cookies._ga;

    if (gaCookie) {
      const parts = gaCookie.split('.');
      if (parts.length === 4) {
        return `${parts[2]}.${parts[3]}`;
      }
    }

    return 'No client id detected';
  };

  // Set client id when _ga cookie is available
  useEffect(() => {
    const gaClientId = getGaClientId();

    setFooterNewsletterInitialValues((prevValues) => ({
      ...prevValues,
      client_id: gaClientId,
    }));
  }, [cookies._ga]);

  // Get fields on load
  useEffect(() => {
    (async () => {
      const data = await fetchFormFields('newsletter');

      setFields(data);
      setSubmitInfo(data.thank_you);

      setWaiting(false);
    })();
  }, []);

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.warn('Recaptcha not ready yet.');
      return null;
    }

    try {
      return await executeRecaptcha('enews');
    } catch (error) {
      console.error('Recaptcha execution failed:', error);
      return null;
    }
  }, [executeRecaptcha]);

  const postForm = async (data, recaptchaToken, callback) => {
    if (!recaptchaToken || recaptchaToken.trim().length < 1) {
      alert('Please fill out the Recaptcha');
      return;
    }

    setWaiting(true);

    try {
      const extraFields = {
        form_reference: 'enews',
        id: 'newsletter-form',
      };

      await postFormData({ ...data, ...extraFields }, recaptchaToken);

      if (callback) callback();

      if (submitInfo.type === 'page') {
        window.location.href = submitInfo.page;
      } else {
        setShowSuccessDialog(true);
      }
    } catch (error) {
      console.error(error);

      if (error.response?.data?.message?.includes('recaptcha')) {
        alert('Invalid recaptcha response');
      }
    }

    setWaiting(false);
  };

  const disable = (errors, values, initialValues) => {
    const hasErrors = Object.keys(errors).length > 0;

    return (
      hasErrors || JSON.stringify(values) === JSON.stringify(initialValues)
    );
  };

  return {
    footerNewsletterInitialValues,
    footerNewsletterValidationSchema,
    disable,
    postForm,
    fields,
    submitInfo,
    waiting,
    showSuccessDialog,
    setShowSuccessDialog,
    handleReCaptchaVerify,
  };
};

export default useNewsletterForm;
